/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/features/auth/hooks/useAuthentication.ts
 * PURPOSE: Custom React hooks for state management
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance)
 * SPRINT GOVERNANCE: Rule 9: Documentation Standards
 *
 *
 * @business-context
 * - Domain: React Hooks, State Management, Vietnamese F&B Business Logic
 * - Impact: Code Reusability, State Consistency, Developer Productivity
 * - Stakeholders: Development Team, Frontend Engineers
 *
 * @technical-architecture
 * - Layer: Business Logic Layer, Custom Hooks
 * - Pattern: React Hooks Pattern, State Management, API Integration
 * - Dependencies: React 18, TypeScript, API Services
 *
 * @cross-references
 * - Design: docs/02-design/05-business-logic/
 *  */

import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store";
import { authService } from "../services/authService";
import type { LoginRequest, OTPVerifyRequest } from "@/types/auth";
import { error_message_extract__parses_error__returns_string } from "@/types/errors";

import type { User } from "@/types/auth";

export interface AuthenticationState {
  // User state
  user: User | null;
  isAuthenticated: boolean;
  accessToken: string | null;

  // UI state
  isLoading: boolean;
  error: string | null;

  // OTP flow state
  otpSent: boolean;
  phoneNumber: string;
}

export interface AuthenticationActions {
  // Primary actions
  sendOTP: (data: LoginRequest) => Promise<void>;
  verifyOTP: (otp: string) => Promise<void>;
  logout: () => void;

  // Utility actions
  resetOTP: () => void;
  clearError: () => void;
  refreshUser: () => Promise<void>;
}

/**
 * Primary authentication hook for all auth operations
 * @returns {Object} Authentication state and actions
 * @returns {User | null} user - Current authenticated user
 * @returns {boolean} isAuthenticated - Whether user is authenticated
 * @returns {string | null} accessToken - JWT access token
 * @returns {Function} sendOTP - Send OTP to phone number
 * @returns {Function} verifyOTP - Verify OTP code
 * @returns {Function} logout - Logout user
 *
 * @example
 * const { user, sendOTP, verifyOTP, logout } = auth_state_use__manages_session__returns_actions();
 *
 * // Send OTP
 * await auth_otp_send__validates_phone__starts_flow({ phone_number: '+84901234567' });
 *
 * // Verify OTP
 * await auth_otp_verify__authenticates_user__sets_tokens('123456');
 */
export function auth_state_use__manages_session__returns_actions() {
  const navigate = useNavigate();
  const {
    user,
    accessToken,
    isAuthenticated,
    setTokens,
    setUser,
    logout: logoutStore,
    setLoading: setAuthLoading,
  } = useAuthStore();

  // Local state
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");

  // Load user data on mount if authenticated
  useEffect(() => {
    if (isAuthenticated && accessToken && !user) {
      user_profile_refresh__fetches_data__updates_state();
    }
  }, [isAuthenticated, accessToken, user]);

  // Refresh user data
  const user_profile_refresh__fetches_data__updates_state =
    useCallback(async () => {
      if (!accessToken) return;

      try {
        const userData =
          await authService.user_profile_get__includes_preferences(accessToken);
        setUser(userData);
      } catch (_error) {
        // If token is invalid, logout
        logoutStore();
      }
    }, [accessToken, setUser, logoutStore]);

  // Send OTP
  const auth_otp_send__validates_phone__starts_flow = useCallback(
    async (data: LoginRequest) => {
      setError(null);
      setIsLoading(true);
      setAuthLoading(true);

      try {
        await authService.auth_otp_send__validates_phone__starts_flow(data);
        setPhoneNumber(data.phone_number);
        setOtpSent(true);
      } catch (_error) {
        setError(error_message_extract__parses_error__returns_string(error));
        throw error;
      } finally {
        setIsLoading(false);
        setAuthLoading(false);
      }
    },
    [setAuthLoading],
  );

  // Verify OTP
  const auth_otp_verify__authenticates_user__sets_tokens = useCallback(
    async (otp: string) => {
      setError(null);
      setIsLoading(true);
      setAuthLoading(true);

      try {
        const data: OTPVerifyRequest = { phone_number: phoneNumber, otp };
        const response =
          await authService.auth_otp_verify__authenticates_user__sets_tokens(
            data,
          );

        // Set tokens and user
        setTokens(response.access_token, response.refresh_token || "");
        setUser(response.user);

        // Navigate to appropriate page based on role
        const redirectTo = response.user.role === "manager" || response.user.role === "admin" 
          ? "/manager" 
          : "/clock";
        navigate(redirectTo);
      } catch (_error) {
        setError(error_message_extract__parses_error__returns_string(error));
        throw error;
      } finally {
        setIsLoading(false);
        setAuthLoading(false);
      }
    },
    [phoneNumber, setTokens, setUser, navigate, setAuthLoading],
  );

  // Logout
  const auth_session_end__clears_tokens__redirects = useCallback(() => {
    logoutStore();
    navigate("/login");
  }, [logoutStore, navigate]);

  // Reset OTP flow
  const otp_flow_reset__clears_state__restarts_process = useCallback(() => {
    setOtpSent(false);
    setPhoneNumber("");
    setError(null);
  }, []);

  // Clear error
  const error_state_clear__resets_error__updates_ui = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    user,
    isAuthenticated,
    accessToken,
    isLoading,
    error,
    otpSent,
    phoneNumber,

    // Actions
    sendOTP: auth_otp_send__validates_phone__starts_flow,
    verifyOTP: auth_otp_verify__authenticates_user__sets_tokens,
    logout: auth_session_end__clears_tokens__redirects,
    resetOTP: otp_flow_reset__clears_state__restarts_process,
    clearError: error_state_clear__resets_error__updates_ui,
    refreshUser: user_profile_refresh__fetches_data__updates_state,
  };
}

// Alias for backward compatibility
export const useAuthentication =
  auth_state_use__manages_session__returns_actions;
export const useAuth = auth_state_use__manages_session__returns_actions;
